// Only catalog identifiers, prices, and quantities are sent to Meta.
const safely = (operation) => {
  try {
    return operation();
  } catch {
    return false;
  }
};

let initialized = false;
let unavailable = false;
let lastPage;
let lastProductView;
const purchases = new Set();
const pixelId = () => (process.env.REACT_APP_META_PIXEL_ID || "").trim();

const init = () => safely(() => {
  if (!pixelId() || typeof window === "undefined" || unavailable) return false;
  if (initialized) return true;
  if (!window.fbq) {
    const fbq = function () {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    window._fbq = fbq;
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.onerror = () => { unavailable = true; };
    document.head.appendChild(script);
  }
  // React Router owns PageViews; disable Meta's automatic history tracking.
  window.fbq.disablePushState = true;
  window.fbq("set", "autoConfig", false, pixelId());
  window.fbq("init", pixelId());
  initialized = true;
  return true;
});

const track = (event, parameters) => safely(() => {
  if (!initialized || unavailable || !pixelId() || typeof window.fbq !== "function") return false;
  window.fbq("trackSingle", pixelId(), event, parameters);
  return true;
});

const validItem = (item) => item && item.id != null &&
  Number.isFinite(Number(item.listPrice)) && Number(item.listPrice) >= 0 &&
  Number.isFinite(Number(item.quantity ?? 1)) && Number(item.quantity ?? 1) > 0;

const itemParameters = (items, value) => {
  const contents = items.map((item) => ({
    id: String(item.id),
    quantity: Number(item.quantity ?? 1),
    item_price: Number(item.listPrice),
  }));
  return {
    content_ids: contents.map((item) => item.id),
    content_type: "product",
    contents,
    value: Number(value),
    currency: "INR",
    num_items: contents.reduce((total, item) => total + item.quantity, 0),
  };
};

const productEvent = (event, product, quantity = 1) => {
  const item = { ...product, quantity };
  if (!validItem(item)) return false;
  return track(event, {
    ...itemParameters([item], Number(product.listPrice) * Number(quantity)),
    content_name: product.title,
  });
};

const metaPixel = {
  init,
  pageView: (url) => safely(() => {
    if (url === lastPage) return false;
    if (!track("PageView")) return false;
    lastPage = url;
    return true;
  }),
  viewContent: (product, visitKey) => safely(() => {
    if (lastProductView?.visitKey === visitKey && lastProductView?.productId === product.id) return false;
    if (!productEvent("ViewContent", product)) return false;
    lastProductView = { visitKey, productId: product.id };
    return true;
  }),
  addToCart: (product, quantity = 1) => safely(() => productEvent("AddToCart", product, quantity)),
  initiateCheckout: (items, total) => safely(() => {
    if (!items.length || !items.every(validItem) || !Number.isFinite(Number(total)) || Number(total) < 0) return false;
    return track("InitiateCheckout", itemParameters(items, total));
  }),
  purchase: (order) => safely(() => {
    // COD is a confirmed order, although payment is collected on delivery.
    if (!order?.id || order.status !== "Placed" ||
      !(order.payment?.method === "cod" || order.payment?.status === "completed") ||
      !order.items?.length || !order.items.every(validItem) ||
      !Number.isFinite(Number(order.amount)) || Number(order.amount) < 0) return false;
    const key = `meta-purchase:${pixelId()}:${order.id}`;
    if (purchases.has(key)) return false;
    try { if (window.localStorage.getItem(key)) return false; } catch { /* Storage can be blocked. */ }
    if (!track("Purchase", itemParameters(order.items, order.amount))) return false;
    purchases.add(key);
    try { window.localStorage.setItem(key, "1"); } catch { /* In-memory deduplication remains active. */ }
    return true;
  }),
};

export default metaPixel;
